import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import OneTimePaymentBanner from '../../components/common/OneTimePaymentBanner';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import StripeCardForm from '../../components/payment/StripeCardForm';
import { FiAlertCircle } from 'react-icons/fi';
import { giftService } from '../../services/giftService';
import SuccessMessage from '../../components/payment/SuccessMessage';
import { logger } from '../../utils/logger';

const GiftContainer = styled.div`
  padding: 3rem 0;
`;

const GiftCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
`;

const GiftHeader = styled.div`
  background-color: var(--primary);
  padding: 2rem;
  text-align: center;
  color: white;
`;

const GiftTitle = styled.h1`
  margin-bottom: 0.5rem;
  font-family: 'Montserrat', sans-serif;
`;

const GiftSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const GiftContent = styled.div`
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: var(--dark);
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif;
  display: flex;
  align-items: center;
  
  .icon {
    margin-right: 0.75rem;
    color: var(--primary);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--dark);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error === "true" && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
  
  /* Add class for error message targeting */
  &.error-message {
    scroll-margin-top: 100px;
  }
`;

const PlanSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const PlanOption = styled.div`
  flex: 1;
  min-width: 200px;
  border: 2px solid ${props => props.selected ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.selected ? 'var(--primary)' : 'var(--primary-light)'};
  }
  
  ${props => props.selected && `
    background-color: rgba(233, 64, 87, 0.05);
    
    &::after {
      content: '✓';
      position: absolute;
      top: 10px;
      right: 10px;
      color: var(--primary);
      font-weight: bold;
    }
  `}
`;

const PlanName = styled.h4`
  margin-bottom: 0.5rem;
  color: var(--dark);
`;

const PlanPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  color: var(--gray);
  font-size: 0.9rem;
  margin-bottom: 0;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #eee;
  margin: 2rem 0;
`;

const PaymentButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s ease, transform 0.2s ease;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
    transform: none;
  }
  
  .spinner {
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const GiftPreview = styled.div`
  background-color: var(--light);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const GiftPreviewHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  .icon {
    font-size: 2rem;
    color: var(--primary);
    margin-right: 1rem;
  }
`;

const GiftPreviewTitle = styled.h4`
  margin: 0;
  color: var(--dark);
`;

const GiftPreviewContent = styled.div`
  background-color: white;
  border: 1px dashed #ddd;
  border-radius: 8px;
  padding: 1.5rem;
  position: relative;
  
  &::after {
    content: 'Preview';
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background-color: var(--light);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    color: var(--gray);
  }
`;

const GiftMessage = styled.p`
  font-style: italic;
  color: var(--dark);
  margin-bottom: 1rem;
  
  &::before {
    content: '"';
  }
  
  &::after {
    content: '"';
  }
`;

const GiftFrom = styled.p`
  margin-bottom: 0;
  text-align: right;
  color: var(--dark);
  
  .from {
    font-weight: normal;
    color: var(--gray);
  }
`;

const OrderSummary = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const OrderSummaryTitle = styled.h4`
  margin-bottom: 1rem;
  color: var(--dark);
`;

const OrderSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: ${props => props.total ? 'none' : '1px solid #eee'};
  font-weight: ${props => props.total ? 'bold' : 'normal'};
  font-size: ${props => props.total ? '1.1rem' : '1rem'};
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const CheckboxInput = styled.input`
  margin-right: 0.75rem;
  margin-top: 0.25rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: var(--dark);
  
  a {
    color: var(--primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const DisclaimerText = styled.p`
  font-size: 0.85rem;
  color: var(--gray);
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const GiftPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Give the Gift of Sleep | Baby Sleep Plan Gift Cards | Dozzy",
    description: "The perfect gift for new parents - personalized baby sleep plans from Dozzy. Help someone you care about get more rest with our gift options.",
    keywords: [
      "baby sleep gift", 
      "new parent gift", 
      "sleep plan gift card",
      "baby shower gift",
      "gift for tired parents",
      "unique baby gift",
      "sleep consultant gift"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Dozzy Sleep Plan Gift Card",
      "description": "Give the gift of sleep with a personalized AI sleep plan for babies and toddlers",
      "offers": [
        {
          "@type": "Offer",
          "name": "Basic Plan Gift",
          "price": "49.00",
          "priceCurrency": "AUD",
          "description": "Gift an AI-generated personalized sleep plan for one child"
        },
        {
          "@type": "Offer",
          "name": "Family Plan Gift",
          "price": "79.00",
          "priceCurrency": "AUD",
          "description": "Gift AI-generated personalized sleep plans for up to 2 children"
        }
      ]
    }
  };
  
  const [formData, setFormData] = useState({
    giverFirstName: '',
    giverLastName: '',
    giverEmail: '',
    recipientFirstName: '',
    recipientLastName: '',
    recipientEmail: '',
    message: 'Wishing you and your little one the gift of peaceful sleep!',
    plan: 'basic',
    agreeTerms: false
  });
  
  // Create ref for error elements
  const errorRef = useRef(null);
  
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({
    complete: false,
    error: null
  });
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Effect to scroll to top when success state changes to true
  useEffect(() => {
    if (isSuccess) {
      logger.debug('Success state changed to true, scrolling to top');
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0; // For Safari
    }
  }, [isSuccess]);
  
  // Remove the useEffect for scrolling since we're handling it directly in validateForm
  
  const plans = {
    basic: {
      name: 'Basic Plan',
      price: '$49',
      priceValue: 49,
      description: 'AI-generated personalized sleep plan for one child',
      details: 'The recipient will receive an email with instructions to create a customized sleep plan for one child using our Artificial Intelligence (AI) technology.'
    },
    family: {
      name: 'Family Plan',
      price: '$79',
      priceValue: 79,
      description: 'AI-generated personalized sleep plans for up to 2 children',
      details: 'The recipient will receive an email with instructions to create customized sleep plans for up to two children using our Artificial Intelligence (AI) technology.'
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing, but don't trigger scrolling
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      // Use this approach to avoid triggering the useEffect for scrolling
      setErrors(newErrors);
    }
  };
  
  const selectPlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      plan
    }));
  };
  
  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setPaymentStatus({
      complete: event.complete,
      error: event.error ? event.error.message : null
    });
    
    // Handle payment errors without triggering scrolling
    if (event.error) {
      const newErrors = { ...errors };
      newErrors.payment = event.error.message;
      setErrors(newErrors);
    } else if (errors.payment) {
      const newErrors = { ...errors };
      delete newErrors.payment;
      setErrors(newErrors);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Define the order of fields for validation (top to bottom on the form)
    const fieldOrder = [
      'giverFirstName',
      'giverLastName',
      'giverEmail',
      'recipientFirstName',
      'recipientLastName',
      'recipientEmail',
      'payment',
      'agreeTerms'
    ];
    
    // Giver information validation
    if (!formData.giverFirstName.trim()) {
      newErrors.giverFirstName = 'First name is required';
    }
    
    if (!formData.giverLastName.trim()) {
      newErrors.giverLastName = 'Last name is required';
    }
    
    if (!formData.giverEmail.trim()) {
      newErrors.giverEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.giverEmail)) {
      newErrors.giverEmail = 'Email is invalid';
    }
    
    // Recipient information validation
    if (!formData.recipientFirstName.trim()) {
      newErrors.recipientFirstName = 'First name is required';
    }
    
    if (!formData.recipientLastName.trim()) {
      newErrors.recipientLastName = 'Last name is required';
    }
    
    if (!formData.recipientEmail.trim()) {
      newErrors.recipientEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Email is invalid';
    }
    
    // Payment validation
    if (!paymentStatus.complete) {
      newErrors.payment = 'Please complete your payment information';
    }
    
    if (paymentStatus.error) {
      newErrors.payment = paymentStatus.error;
    }
    
    // Terms validation
    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the Terms and Conditions';
    }
    
    // Set the errors state
    setErrors(newErrors);
    
    // Find the first error based on field order
    if (Object.keys(newErrors).length > 0) {
      window.hasScrolledToError = false;
      
      // Find the first field with an error based on our defined order
      const firstErrorField = fieldOrder.find(field => newErrors[field]);
      
      if (firstErrorField) {
        // Set a timeout to allow the DOM to update with error messages
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            logger.debug('Scrolling to first error field:', firstErrorField);
          } else {
            // Fallback to the error message
            const errorMessages = document.querySelectorAll('.error-message');
            if (errorMessages.length > 0) {
              errorMessages[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
              logger.debug('Scrolling to first error message');
            }
          }
        }, 100);
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset any previous errors
    setErrors({});
    
    if (!validateForm()) {
      return;
    }
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      const newErrors = { ...errors };
      newErrors.payment = 'Payment system is not ready. Please try again.';
      setErrors(newErrors);
      return;
    }
    
    setIsProcessing(true);
    setPaymentError('');
    
    try {
      // Get card element from Stripe Elements
      const cardElement = elements.getElement('cardNumber');
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${formData.giverFirstName} ${formData.giverLastName}`,
          email: formData.giverEmail
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Process gift through API
      const giftData = {
        giverFirstName: formData.giverFirstName,
        giverLastName: formData.giverLastName,
        giverEmail: formData.giverEmail,
        recipientFirstName: formData.recipientFirstName,
        recipientLastName: formData.recipientLastName,
        recipientEmail: formData.recipientEmail,
        message: formData.message,
        plan: formData.plan,
        paymentId: paymentMethod.id
      };
      
      // Call the gift API
      const response = await giftService.processGift(giftData);
      
      // Show success message
      setIsSuccess(true);
      
      // Scroll to top of the page - using multiple methods for cross-browser compatibility
      try {
        // Method 1: Modern smooth scrolling
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
        
        // Method 2: Force scroll for browsers that might not support smooth scrolling
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0; // For Safari
        }, 100);
        
        logger.debug('Scrolled to top after gift submission');
      } catch (scrollError) {
        logger.error('Error scrolling to top:', scrollError);
        // Fallback method
        window.scrollTo(0, 0);
      }
      
    } catch (error) {
      logger.error('Payment failed:', error);
      setPaymentError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Calculate selected plan
  const selectedPlan = plans[formData.plan];
  
  if (isSuccess) {
    return (
      <>
        <SEO 
          title="Gift Sent Successfully | Dozzy"
          description="Your gift of a personalized sleep plan has been sent successfully."
          canonicalUrl="https://dozzy.au/gift"
          ogType="website"
        />
        
        <section className="py-5 bg-primary text-white text-center">
          <div className="container py-2">
            <h1>Gift Sent Successfully</h1>
            <p className="mx-auto" style={{ maxWidth: '700px' }}>
              Thank you for your purchase
            </p>
          </div>
        </section>
        
        <div className="container py-5">
          <SuccessMessage 
            title="Gift Sent Successfully!"
            message={`Your gift has been sent to ${formData.recipientFirstName} at ${formData.recipientEmail}. They will receive an email with instructions on how to access their personalized sleep plan.`}
            primaryButtonText="Return to Home"
            primaryButtonLink="/"
            secondaryButtonText="Learn More About Dozzy"
            secondaryButtonLink="/about"
          />
        </div>
      </>
    );
  }
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/gift"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Give the Gift of Sleep</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Help someone you care about get the rest they deserve with a personalized AI sleep plan
          </p>
        </div>
      </section>
      
      <div className="container">
        <GiftContainer>
          <GiftCard>
         
            <GiftContent>
              {/* How It Works Section */}
              <div className="alert alert-light mb-4">
                <h5 className="mb-2"><i className="bi bi-info-circle me-2"></i>How It Works</h5>
                <p className="mb-0">
                  Purchase a gift sleep plan for a parent, grandparent, or caregiver in your life who could use better sleep. Add a personal message, and we'll send an email to your loved one with instructions to create their personalized sleep plan. They'll get immediate access to our AI-powered platform that creates customized sleep guidance based on their child's unique needs.
                </p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <FormSection>
                  <SectionTitle>
                    <span className="icon"><i className="bi bi-gift"></i></span>
                    Choose a Plan
                  </SectionTitle>
                  
                  <PlanSelector>
                    <PlanOption 
                      selected={formData.plan === 'basic'} 
                      onClick={() => selectPlan('basic')}
                    >
                      <PlanName>Basic Plan</PlanName>
                      <PlanPrice>$49 <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>(one-time payment)</span></PlanPrice>
                      <PlanDescription>{plans.basic.description}</PlanDescription>
                      <PlanDescription style={{ marginTop: '10px' }}>{plans.basic.details}</PlanDescription>
                    </PlanOption>
                    
                    <PlanOption 
                      selected={formData.plan === 'family'} 
                      onClick={() => selectPlan('family')}
                    >
                      <PlanName>Family Plan</PlanName>
                      <PlanPrice>$79 <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>(one-time payment)</span></PlanPrice>
                      <PlanDescription>{plans.family.description}</PlanDescription>
                      <PlanDescription style={{ marginTop: '10px' }}>{plans.family.details}</PlanDescription>
                    </PlanOption>
                  </PlanSelector>
                </FormSection>
                
                <FormSection>
                  <SectionTitle>
                    <span className="icon"><i className="bi bi-person-heart"></i></span>
                    Your Information
                  </SectionTitle>
                  
                  <FormRow>
                    <FormGroup>
                      <FormLabel htmlFor="giverFirstName">Your First Name</FormLabel>
                      <FormInput
                        type="text"
                        id="giverFirstName"
                        name="giverFirstName"
                        value={formData.giverFirstName}
                        onChange={handleChange}
                        error={errors.giverFirstName ? "true" : undefined}
                        aria-describedby="giverFirstNameError"
                      />
                      {errors.giverFirstName && <ErrorMessage id="giverFirstNameError" className="error-message" ref={errors.giverFirstName === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.giverFirstName}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                      <FormLabel htmlFor="giverLastName">Your Last Name</FormLabel>
                      <FormInput
                        type="text"
                        id="giverLastName"
                        name="giverLastName"
                        value={formData.giverLastName}
                        onChange={handleChange}
                        error={errors.giverLastName ? "true" : undefined}
                        aria-describedby="giverLastNameError"
                      />
                      {errors.giverLastName && <ErrorMessage id="giverLastNameError" className="error-message" ref={errors.giverLastName === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.giverLastName}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormGroup>
                    <FormLabel htmlFor="giverEmail">Your Email</FormLabel>
                    <FormInput
                      type="email"
                      id="giverEmail"
                      name="giverEmail"
                      value={formData.giverEmail}
                      onChange={handleChange}
                      error={errors.giverEmail ? "true" : undefined}
                      aria-describedby="giverEmailError"
                    />
                    {errors.giverEmail && <ErrorMessage id="giverEmailError" className="error-message" ref={errors.giverEmail === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.giverEmail}</ErrorMessage>}
                  </FormGroup>
                </FormSection>
                
                <FormSection>
                  <SectionTitle>
                    <span className="icon"><i className="bi bi-envelope-heart"></i></span>
                    Recipient Information
                  </SectionTitle>
                  
                  <FormRow>
                    <FormGroup>
                      <FormLabel htmlFor="recipientFirstName">Recipient's First Name</FormLabel>
                      <FormInput
                        type="text"
                        id="recipientFirstName"
                        name="recipientFirstName"
                        value={formData.recipientFirstName}
                        onChange={handleChange}
                        error={errors.recipientFirstName ? "true" : undefined}
                      />
                      {errors.recipientFirstName && <ErrorMessage className="error-message" ref={errors.recipientFirstName === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.recipientFirstName}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                      <FormLabel htmlFor="recipientLastName">Recipient's Last Name</FormLabel>
                      <FormInput
                        type="text"
                        id="recipientLastName"
                        name="recipientLastName"
                        value={formData.recipientLastName}
                        onChange={handleChange}
                        error={errors.recipientLastName ? "true" : undefined}
                      />
                      {errors.recipientLastName && <ErrorMessage className="error-message" ref={errors.recipientLastName === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.recipientLastName}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormGroup>
                    <FormLabel htmlFor="recipientEmail">Recipient's Email</FormLabel>
                    <FormInput
                      type="email"
                      id="recipientEmail"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleChange}
                      error={errors.recipientEmail ? "true" : undefined}
                    />
                    {errors.recipientEmail && <ErrorMessage className="error-message" ref={errors.recipientEmail === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.recipientEmail}</ErrorMessage>}
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="message">Personal Message (optional)</FormLabel>
                    <FormTextarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Add a personal message to the recipient"
                    />
                  </FormGroup>
                </FormSection>
                
                <GiftPreview>
                  <GiftPreviewHeader>
                    <span className="icon"><i className="bi bi-envelope-paper-heart"></i></span>
                    <GiftPreviewTitle>Gift Preview</GiftPreviewTitle>
                  </GiftPreviewHeader>
                  
                  <GiftPreviewContent>
                    <h4>You've received a Dozzy Sleep Plan!</h4>
                    <p>{formData.recipientFirstName}, someone special has gifted you an AI-powered sleep plan from Dozzy.au.</p>
                    
                    <GiftMessage>{formData.message}</GiftMessage>
                    
                    <GiftFrom>
                      <span className="from">From:</span> {formData.giverFirstName} {formData.giverLastName}
                    </GiftFrom>
                  </GiftPreviewContent>
                </GiftPreview>
                
                <Divider />
                
                <FormSection>
                  <SectionTitle>
                    <span className="icon"><i className="bi bi-credit-card"></i></span>
                    Payment Details
                  </SectionTitle>
                  
                  <StripeCardForm 
                    onChange={handleCardChange}
                    errors={errors.payment ? { payment: errors.payment } : null}
                  />
                  
                  {errors.payment && <ErrorMessage id="paymentError" className="error-message" ref={errors.payment === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.payment}</ErrorMessage>}
                  
                  {paymentError && <ErrorMessage className="error-message" ref={paymentError && Object.keys(errors).length === 0 ? errorRef : null}><FiAlertCircle />{paymentError}</ErrorMessage>}
                  
                  <OrderSummary>
                    <OrderSummaryTitle>Order Summary</OrderSummaryTitle>
                    <OrderSummaryRow>
                      <span>{selectedPlan.name}</span>
                      <span>{selectedPlan.price}</span>
                    </OrderSummaryRow>
                    <OrderSummaryRow>
                      <span>GST (included)</span>
                      <span>${(selectedPlan.priceValue * 0.1).toFixed(2)}</span>
                    </OrderSummaryRow>
                    <OrderSummaryRow total>
                      <span>Total</span>
                      <span>{selectedPlan.price}</span>
                    </OrderSummaryRow>
                  </OrderSummary>
                  
                  <CheckboxContainer>
                    <CheckboxInput
                      type="checkbox"
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                    />
                    <CheckboxLabel htmlFor="agreeTerms">
                      I have read and agree to the <Link to="/terms-and-conditions" target="_blank">Terms and Conditions</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>. I understand that my data will be encrypted and stored securely.
                    </CheckboxLabel>
                  </CheckboxContainer>
                  {errors.agreeTerms && <ErrorMessage className="error-message" ref={errors.agreeTerms === Object.values(errors)[0] ? errorRef : null}><FiAlertCircle />{errors.agreeTerms}</ErrorMessage>}
                  
                  <PaymentButton type="submit" disabled={!stripe || isProcessing}>
                    {isProcessing ? (
                      <>
                        <span className="spinner">⟳</span> Processing...
                      </>
                    ) : (
                      <>Send Gift - {plans[formData.plan].price} (one-time payment)</>
                    )}
                  </PaymentButton>
                </FormSection>
              </form>
            </GiftContent>
          </GiftCard>
          
          <DisclaimerText>
            <div class="mt-4"></div>
            <strong>Important:</strong> Dozzy.au provides AI-generated personalized sleep plans based on guidelines from respected Australian organizations. 
            Our service is not medical advice. 
          </DisclaimerText>
        </GiftContainer>
      </div>
    </>
  );
};

export default GiftPage;
